import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { achievements } from "@/data/achievements";
import type { UserAchievements } from "@/data/achievements";
import { useNavigate } from "react-router-dom";

export function useGamification() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Listen for auth changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // Clear any cached data
        queryClient.clear();
        navigate('/auth');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient, navigate]);

  // Get user achievements and their progress
  const { data: userAchievements = [] } = useQuery({
    queryKey: ['user-achievements'],
    queryFn: async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          console.log('No active session found, redirecting to auth');
          navigate('/auth');
          return [];
        }

        console.log('Fetching achievements for user:', session.user.id);
        const { data, error } = await supabase
          .from('user_achievements')
          .select('*')
          .eq('user_id', session.user.id);

        if (error) {
          console.error('Error fetching achievements:', error);
          if (error.code === 'PGRST301') {
            toast.error('Please log in to view achievements');
            navigate('/auth');
          } else {
            toast.error('Failed to fetch achievements');
          }
          throw error;
        }

        console.log('Fetched achievements:', data);
        return data || [];
      } catch (err) {
        console.error('Error in achievement query:', err);
        return [];
      }
    },
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  // Get user streaks
  const { data: userStreaks = [] } = useQuery({
    queryKey: ['user-streaks'],
    queryFn: async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          console.log('No active session found, redirecting to auth');
          navigate('/auth');
          return [];
        }

        console.log('Fetching streaks for user:', session.user.id);
        const { data, error } = await supabase
          .from('user_streaks')
          .select('*')
          .eq('user_id', session.user.id);

        if (error) {
          console.error('Error fetching streaks:', error);
          if (error.code === 'PGRST301') {
            toast.error('Please log in to view streaks');
            navigate('/auth');
          } else {
            toast.error('Failed to fetch streaks');
          }
          throw error;
        }

        console.log('Fetched streaks:', data);
        return data || [];
      } catch (err) {
        console.error('Error in streaks query:', err);
        return [];
      }
    },
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  const updateAchievements = useCallback(async (achievementId: string) => {
    try {
      console.log('Attempting to update achievement:', achievementId);
      
      const achievement = achievements.find(a => a.id === achievementId);
      if (!achievement) {
        console.error('Achievement not found:', achievementId);
        return;
      }

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user?.id) {
        console.error('No active session found');
        return;
      }

      // Check if achievement already exists
      const { data: existingAchievement } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', session.session.user.id)
        .eq('achievement_id', achievementId)
        .maybeSingle();

      if (existingAchievement) {
        console.log('Achievement already earned:', achievementId);
        return;
      }

      console.log('Creating new achievement:', achievementId);

      const newAchievement = {
        achievement_id: achievementId,
        earned_at: new Date().toISOString(),
        user_id: session.session.user.id,
        metadata: {
          progress: 100,
          points: achievement.points
        }
      };

      const { error } = await supabase
        .from('user_achievements')
        .insert([newAchievement]);

      if (error) {
        console.error('Error updating achievement:', error);
        toast.error('Failed to update achievement');
        return;
      }

      console.log('Achievement successfully updated:', achievementId);
      await queryClient.invalidateQueries({ queryKey: ['user-achievements'] });
      
      toast.success(`Achievement Unlocked: ${achievement.title}`, {
        description: achievement.description
      });
    } catch (err) {
      console.error('Error in updateAchievements:', err);
    }
  }, [queryClient]);

  const updateStreak = useCallback(async (subject?: string) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session?.user?.id) {
        console.error('No active session found');
        return;
      }

      console.log('Updating streak for user:', session.session.user.id);
      const { data: streak, error } = await supabase
        .from('user_streaks')
        .select('*')
        .eq('user_id', session.session.user.id)
        .eq('subject', subject || '')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching streak:', error);
        return;
      }

      const lastActive = streak?.last_activity_date?.split('T')[0] || today;
      const daysSinceLastActive = Math.floor(
        (new Date(today).getTime() - new Date(lastActive).getTime()) / (1000 * 60 * 60 * 24)
      );

      const newStreak = {
        user_id: session.session.user.id,
        current_streak: daysSinceLastActive <= 1 ? (streak?.current_streak || 0) + 1 : 1,
        longest_streak: streak ? Math.max(streak.longest_streak || 0, (streak.current_streak || 0) + 1) : 1,
        last_activity_date: today,
        subject: subject || null,
        streak_type: 'daily'
      };

      const { error: updateError } = await supabase
        .from('user_streaks')
        .upsert(newStreak);

      if (updateError) {
        console.error('Error updating streak:', updateError);
        toast.error('Failed to update streak');
        return;
      }

      // Check if streak achievement should be unlocked
      if (newStreak.current_streak >= 7) {
        await updateAchievements('consistent_learner');
      }

      await queryClient.invalidateQueries({ queryKey: ['user-streaks'] });
      
      toast.success(`Streak Updated: ${newStreak.current_streak} days`, {
        description: `Keep learning to maintain your streak!`
      });
    } catch (err) {
      console.error('Error in updateStreak:', err);
      toast.error('Failed to update streak');
    }
  }, [queryClient, updateAchievements]);

  // Format achievements for the UI
  const formattedAchievements = Object.fromEntries(
    userAchievements.map((ua: any) => [
      ua.achievement_id,
      {
        earned: true,
        earnedAt: ua.earned_at,
        progress: ua.metadata?.progress || 100
      }
    ])
  ) as UserAchievements;

  const checkAchievements = useCallback(async (score: number, totalQuestions: number, difficulty: string) => {
    console.log('Checking achievements with score:', score, 'total:', totalQuestions, 'difficulty:', difficulty);
    
    try {
      for (const achievement of achievements) {
        console.log('Checking achievement:', achievement.id);
        
        if (achievement.condition(score, totalQuestions, difficulty)) {
          console.log('Achievement condition met for:', achievement.id);
          await updateAchievements(achievement.id);
        }
      }

      // Check for difficulty-specific achievements
      if (difficulty === "easy") {
        await updateAchievements("easy_master");
      } else if (difficulty === "medium") {
        await updateAchievements("medium_achiever");
      } else if (difficulty === "hard") {
        await updateAchievements("hard_conqueror");
      }
    } catch (err) {
      console.error('Error in checkAchievements:', err);
    }
  }, [updateAchievements]);

  // Methods for coding and flashcard achievements
  const markCodingComplete = useCallback(async () => {
    try {
      await updateAchievements("code_master");
    } catch (err) {
      console.error('Error in markCodingComplete:', err);
    }
  }, [updateAchievements]);

  const markFlashcardProgress = useCallback(async (count: number) => {
    try {
      console.log(`Marking flashcard progress with count: ${count}`);
      
      // First check for memory_master (requires 10 cards)
      if (count >= 10) {
        await updateAchievements("memory_master");
      }
      
      // Then check for flashcard_guru (requires 20 cards)
      if (count >= 20) {
        await updateAchievements("flashcard_guru");
      }
    } catch (err) {
      console.error('Error in markFlashcardProgress:', err);
    }
  }, [updateAchievements]);

  return {
    achievements: formattedAchievements,
    updateAchievements,
    userStreaks,
    dailyMissions: [],
    updateStreak,
    checkAchievements,
    markCodingComplete,
    markFlashcardProgress,
    completeMission: () => {}
  };
}
